import React, { useEffect, useState } from "react";
import "./Banners.css";
import config from "../../../../config";
import { getAuth, signInAnonymously } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../firebase";
const BANNER_TYPES = ["DISCOUNT", "COLLECTION", "ANNOUNCEMENT"];
const PLACEMENTS = [
  "HOME",
  "LANDING",
  "CATEGORY_BOYS",
  "CATEGORY_GIRLS",
  "CATEGORY_MEN",
  "CATEGORY_LADIES",
];
const ACTION_TYPES = [
  "CAMPAIGN",
  "NONE"
];

export default function CreateBannerModal({ onClose, onSuccess, initialData }) {
  const [form, setForm] = useState({
    title: initialData?.title || "",
    subtitle: initialData?.subtitle || "",
    imageUrl: initialData?.imageUrl || "",
    bannerType: initialData?.bannerType || "DISCOUNT",
    placement: initialData?.placement || "HOME",
    actionType: initialData?.actionType || "CAMPAIGN",
    priority: initialData?.priority || 10,
    active: initialData?.active ?? true,
    campaignId: initialData?.campaign?.id || initialData?.campaignId || "",
  });

  const [campaigns, setCampaigns] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {

    if (!initialData) return;

    setForm({
      title: initialData.title || "",
      subtitle: initialData.subtitle || "",
      imageUrl: initialData.imageUrl || "",
      bannerType: initialData.bannerType || "DISCOUNT",
      placement: initialData.placement || "HOME",
      actionType: initialData.actionType || "CAMPAIGN",
      priority: initialData.priority ?? 10,
      active: initialData.active ?? true,
      campaignId:
        initialData.campaign?.id ||
        initialData.campaignId ||
        "",
    });

  }, [initialData]);

  /* =========================
     FETCH CAMPAIGNS
     ========================= */

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/admin/campaigns/summary`);
        if (res.ok) {
          setCampaigns(await res.json());
        } else {
          console.error("Failed to fetch campaigns");
          setCampaigns([]);
        }
      } catch (err) {
        console.error("Failed to load campaigns", err);
        setCampaigns([]);
      }
    };
    fetchCampaigns();
  }, []);

  /* =========================
     HELPERS
     ========================= */

  const handleChange = (field, value) => {

    setForm(prev => ({
      ...prev,
      [field]: value
    }));

  };


  /* =========================
     FIREBASE IMAGE UPLOAD
     ========================= */

  const handleBannerImageUpload = async (file) => {
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const userId = auth.currentUser.uid;
      const filename = `banner_${Date.now()}.jpg`;

      const storageRef = ref(
        storage,
        `banner-images/${userId}/${filename}`
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange("imageUrl", url);
    } catch (err) {
      console.error("Banner upload failed", err);
      setUploadError("Failed to upload banner image.");
    } finally {
      setUploading(false);
    }
  };

  /* =========================
     SAVE BANNER
     ========================= */

  const handleSave = async () => {
    if (!form.title) {
      alert("Title is mandatory");
      return;
    }

    if (!initialData && !form.imageUrl) {
      alert("Banner image is mandatory");
      return;
    }

    if (form.actionType === "CAMPAIGN" && !form.campaignId) {
      setValidationError("Campaign selection is mandatory");
      return;
    }

    setValidationError("");

    const bannerData = {
      title: form.title,
      subtitle: form.subtitle,
      imageUrl: form.imageUrl,
      bannerType: form.bannerType,
      placement: form.placement,
      actionType: form.actionType,
      campaignId:
        form.actionType === "CAMPAIGN"
          ? Number(form.campaignId)
          : null,
      actionPayload: null,
      priority: form.priority,
      active: form.active
    };

    try {
      const res = await fetch(
        initialData
          ? `${config.baseUrl}/admin/banners/${initialData.id}`
          : `${config.baseUrl}/admin/banners`,
        {
          method: initialData ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bannerData),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to save banner");
      }

      onSuccess && onSuccess();
    } catch (err) {
      alert("Error saving banner");
      console.error(err);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h3>{initialData ? "Edit Banner" : "Create New Banner"}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* IMAGE */}
          <div className="form-group">
            <label>Banner Image</label>
            <label className="upload-label">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  handleBannerImageUpload(e.target.files?.[0])
                }
              />
              <div className="upload-box">
                {uploading ? "Uploading..." : "📤 Upload Banner Image"}
              </div>
            </label>

            {uploadError && <div className="error-message">{uploadError}</div>}

            {form.imageUrl && (
              <div className="banner-preview-frame">
                <img src={form.imageUrl} alt="Preview" />
                <small>Mobile preview (3:4)</small>
              </div>
            )}
          </div>

          {/* FORM */}
          <div className="form-grid">
            <div className="form-group">
              <label>Title</label>
              <input
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Subtitle</label>
              <input
                value={form.subtitle}
                onChange={(e) => handleChange("subtitle", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Banner Type</label>
              <select
                value={form.bannerType}
                onChange={(e) =>
                  handleChange("bannerType", e.target.value)
                }
              >
                {BANNER_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Placement</label>
              <select
                value={form.placement}
                onChange={(e) =>
                  handleChange("placement", e.target.value)
                }
              >
                {PLACEMENTS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Action Type</label>
              <select
                value={form.actionType}
                onChange={(e) =>
                  handleChange("actionType", e.target.value)
                }
              >
                {ACTION_TYPES.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <input
                type="number"
                value={form.priority}
                onChange={(e) =>
                  handleChange("priority", Number(e.target.value))
                }
              />
            </div>
          </div>

          {/* CAMPAIGN PAYLOAD */}
          {form.actionType === "CAMPAIGN" && (
            <div className="payload-box">
              <h4>Campaign Selection</h4>

              <div className="form-grid">
                <div className="form-group">
                  <label>Campaign *</label>
                  <select
                    value={form.campaignId}
                    onChange={(e) =>
                      handleChange("campaignId", e.target.value)
                    }
                  >
                    <option value="">Select Campaign</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.ruleCount} Rules • {campaign.productCount} Products)
                      </option>
                    ))}
                  </select>
                  {validationError && (
                    <div className="error-message">{validationError}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ACTIVE */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  handleChange("active", e.target.checked)
                }
              />
              <span>{form.active ? "Active" : "Inactive"}</span>
            </label>
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={uploading}
          >
            Save Banner
          </button>
        </div>
      </div>
    </div>
  );
}
